/**
 * Centralized error logging utility that sanitizes sensitive data
 * to prevent data leakage in console logs
 */

interface SanitizedError {
  message: string;
  stack?: string;
  name?: string;
  statusCode?: number;
  statusText?: string;
}

/**
 * Sanitizes an error object to remove sensitive data
 */
function sanitizeError(error: unknown): SanitizedError {
  const sanitized: SanitizedError = {
    message: "Unknown error",
  };

  if (error instanceof Error) {
    sanitized.message = error.message || "Unknown error";
    sanitized.stack = error.stack;
    sanitized.name = error.name;
  } else if (typeof error === "string") {
    sanitized.message = error;
  } else if (error && typeof error === "object") {
    // Check if it's an axios error or similar with response data
    const errorObj = error as any;

    // Extract safe error message
    if (errorObj.message) {
      sanitized.message = String(errorObj.message);
    } else if (errorObj.error) {
      sanitized.message = String(errorObj.error);
    } else if (errorObj.detail) {
      sanitized.message = String(errorObj.detail);
    }

    // Extract status code if available (safe to log)
    if (errorObj.status) {
      sanitized.statusCode = errorObj.status;
    } else if (errorObj.response?.status) {
      sanitized.statusCode = errorObj.response.status;
      sanitized.statusText = errorObj.response.statusText;
    }

    // Extract stack trace if available
    if (errorObj.stack) {
      sanitized.stack = String(errorObj.stack);
    }
  }

  return sanitized;
}

/**
 * Checks if a string contains sensitive patterns (tokens, passwords, etc.)
 */
function containsSensitiveData(str: string): boolean {
  const sensitivePatterns = [
    /token/i,
    /password/i,
    /secret/i,
    /auth/i,
    /bearer/i,
    /api[_-]?key/i,
    /access[_-]?token/i,
    /refresh[_-]?token/i,
    /mirsal[_-]?access[_-]?token/i,
  ];

  return sensitivePatterns.some((pattern) => pattern.test(str));
}

/**
 * Sanitizes a message string to remove sensitive data
 */
function sanitizeMessage(message: string): string {
  // Ensure message is a string
  if (typeof message !== 'string') {
    message = String(message || 'Unknown error');
  }

  // If message contains sensitive keywords, return a generic message
  if (containsSensitiveData(message)) {
    return "Error occurred (sensitive data removed)";
  }

  // Remove any token-like patterns (long alphanumeric strings)
  const tokenPattern = /[A-Za-z0-9_-]{20,}/g;
  let sanitized = message.replace(tokenPattern, "[REDACTED]");

  // Remove any JSON-like structures that might contain tokens
  const jsonTokenPattern = /"([^"]*token[^"]*)"\s*:\s*"[^"]{20,}"/gi;
  sanitized = sanitized.replace(jsonTokenPattern, '"$1": "[REDACTED]"');

  return sanitized;
}

/**
 * Safely logs an error without exposing sensitive data
 *
 * @param message - Error message (will be sanitized)
 * @param error - Optional error object (will be sanitized)
 */
export function logError(message: string | unknown, error?: Error | unknown): void {
  // Ensure message is a string before sanitizing
  const messageStr = typeof message === 'string' ? message : String(message || 'Unknown error');
  const sanitizedMessage = sanitizeMessage(messageStr);
  const sanitizedError = error ? sanitizeError(error) : null;

  if (sanitizedError) {
    console.error(sanitizedMessage, {
      message: sanitizedError.message,
      name: sanitizedError.name,
      statusCode: sanitizedError.statusCode,
      statusText: sanitizedError.statusText,
      // Only include stack trace in development
      ...(process.env.NODE_ENV === "development" && sanitizedError.stack
        ? { stack: sanitizedError.stack }
        : {}),
    });
  } else {
    console.error(sanitizedMessage);
  }
}

/**
 * Logs a warning without sensitive data
 */
export function logWarning(message: string, data?: unknown): void {
  const sanitizedMessage = sanitizeMessage(message);

  if (data && typeof data === "object") {
    // Only log safe properties
    const safeData: Record<string, unknown> = {};
    Object.keys(data).forEach((key) => {
      if (!containsSensitiveData(key)) {
        const value = (data as Record<string, unknown>)[key];
        if (typeof value !== "string" || !containsSensitiveData(value)) {
          safeData[key] = value;
        }
      }
    });
    console.warn(
      sanitizedMessage,
      Object.keys(safeData).length > 0 ? safeData : ""
    );
  } else {
    console.warn(sanitizedMessage);
  }
}